import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import type { VersionThreadMessage } from "../../types/thread";

import * as actions from "./action-creators";
import type { ChatState } from "./types";

const INITIAL_CHAT_STATE: ChatState = {
    isInitialChatState: false,
    text: "",
    messages: [],
    messageErrors: {},
    userMessageCount: 0,
    userMessageData: {
        message: "",
    },
};

const chatReducer = createReducer<ChatState, ActionType<typeof actions>>(INITIAL_CHAT_STATE)
    .handleAction(
        actions.setChatText,
        (state, { payload }): ChatState => ({
            ...state,
            text: payload,
        }),
    )
    .handleAction(
        actions.setMessages,
        (state, { payload }): ChatState => ({
            ...state,
            messages: payload,
        }),
    )
    .handleAction(
        actions.setIsInitialChatState,
        (state, { payload }): ChatState => ({
            ...state,
            isInitialChatState: payload,
        }),
    )
    .handleAction(
        actions.addMessage,
        (state, { payload }): ChatState => ({
            ...state,
            messages: [...state.messages, payload],
        }),
    )
    .handleAction(
        actions.addHumanMessage,
        (state, { payload }): ChatState => ({
            ...state,
            messages: [...state.messages, { type: "human", content: payload }],
        }),
    )
    .handleAction(actions.addAgentMessage, (state, { payload }): ChatState => {
        const messageExists = state.messages.some((msg) => msg.type === "ai" && msg.id === payload.id);

        if (messageExists) {
            return state;
        }

        return {
            ...state,
            messages: [...state.messages, { ...payload, type: "ai" }],
        };
    })
    .handleAction(actions.addVersionMessage, (state, { payload }): ChatState => {
        const { message, disabledVersions = [] } = payload;
        const nextMsg: VersionThreadMessage = {
            type: "version",
            ...message,
        };

        const updateMessagesDisabledState = state.messages.map((msg) => {
            if (msg.type !== "version") {
                return msg;
            }
            const isDisabled = disabledVersions.includes(msg.id);
            return { ...msg, isDisabled };
        });

        return { ...state, messages: [...updateMessagesDisabledState, nextMsg] };
    })
    .handleAction(
        actions.setMessageError,
        (state, { payload }): ChatState => ({
            ...state,
            messageErrors: {
                ...state.messageErrors,
                [payload.messageId]: payload.error,
            },
        }),
    )
    .handleAction(
        actions.setUserMessageData,
        (state, { payload }): ChatState => ({
            ...state,
            userMessageCount: state.userMessageCount + 1,
            userMessageData: payload,
        }),
    )
    .handleAction(actions.clearAiAgentChatState, (_state): ChatState => INITIAL_CHAT_STATE)
    .handleAction(
        actions.clearMessages,
        (state): ChatState => ({
            ...state,
            messages: INITIAL_CHAT_STATE.messages,
            messageErrors: INITIAL_CHAT_STATE.messageErrors,
        }),
    )
    .handleAction(
        actions.clearChatText,
        (state): ChatState => ({
            ...state,
            text: INITIAL_CHAT_STATE.text,
        }),
    );
export default chatReducer;
